import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useState } from "react";

import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { formatNumber } from "src/utils/number/numberUtils";

import { useSAL2060_R01Store } from "./Store_SAL2060_R01";
import { defaultSAL2060_R01DetailDto, SAL2060_R01DetailDto } from "./Dto_SAL2060_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'SAL2060_R01-detail-grid-column-state';

interface DetailTable_SAL2060_R01Props {
    gridRef: React.RefObject<AgGridReact<SAL2060_R01DetailDto> | null>;
}

const DetailTable_SAL2060_R01 = (props: DetailTable_SAL2060_R01Props) => {
    const { gridRef } = props;

    // ========================================
    // Store
    // ========================================
    const { detailList, detailColumnState, setDetailColumnState } = useSAL2060_R01Store();

    // ========================================
    // State
    // ========================================
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    // ========================================
    // Grid 설정
    // ========================================
    const {
        settingRowIndex,
        createRowClassRules,
        paddedGridData,
    } = useGridSettingContent({
        gridRef,
        key: 'salBillNo',
        gridData: detailList,
        defaultGridData: {} as SAL2060_R01DetailDto,
        selectedRow: undefined,
    });

    // ========================================
    // Row ID 생성
    // ========================================
    const getRowId = useCallback((params: any) => {
        const data = params.data;
        if (data) {
            return `${data.salBillNo || ''}_${data.itemCd || ''}`;
        }
        return params.id;
    }, []);

    // ========================================
    // Cell Style
    // ========================================
    const getCellStyle = useCallback((params: any, key: string) => onlyTouchGridStyle(key, 'salBillNo')(params), []);

    // ========================================
    // Header Names
    // ========================================
    const headerNames: Record<string, string> = {
        rowIndex: "",
        salBillNo: '매출번호',
        salesDt: '매출일자',
        itemCd: '품목코드',
        itemNm: '품목명',
        itemSpec: '규격',
        outputCd: '출력코드',
        unit: '단위',
        salesQty: '수량',
        salesPrice: '단가',
        supplyAmt: '공급가',
        vatAmt: '부가세',
        itemAmt: '금액',
        remark: '비고',
    };

    // ========================================
    // Column 정의
    // ========================================
    const baseColumnDefs: ColDef<SAL2060_R01DetailDto>[] = useMemo(
        () => [
            ...getDefaultRowIndexColumnConfig(),
            // {
            //     headerName: '매출번호',
            //     colId: 'salBillNo',
            //     field: 'salBillNo',
            //     width: 150,
            //     cellStyle: (params) => getCellStyle(params, 'salBillNo'),
            //     headerClass: aggridHeaderClass,
            // },
            {
                headerName: '매출일자',
                colId: 'salesDt',
                field: 'salesDt',
                width: 120,
                cellStyle: (params) => getCellStyle(params, 'salesDt'),
                valueFormatter: (params: any) => {
                    if (!params.value) return '';
                    try {
                        const date = new Date(params.value);
                        return formatDate(date);
                    } catch {
                        return params.value;
                    }
                },
                comparator: cellDateComparator,
            },
            {
                headerName: '품목코드',
                colId: 'itemCd',
                field: 'itemCd',
                width: 150,
                cellStyle: (params) => getCellStyle(params, 'itemCd'),
                headerClass: aggridHeaderClass,
            },
            {
                headerName: '품목명',
                colId: 'itemNm',
                field: 'itemNm',
                width: 180,
                cellStyle: (params) => getCellStyle(params, 'itemNm'),
                headerClass: aggridHeaderClass,
            },
            {
                headerName: '규격',
                colId: 'itemSpec',
                field: 'itemSpec',
                width: 150,
                cellStyle: (params) => getCellStyle(params, 'itemSpec'),
                headerClass: aggridHeaderClass,
            },
            {
                headerName: '단위',
                colId: 'unit',
                field: 'unit',
                width: 80,
                cellStyle: (params) => getCellStyle(params, 'unit'),
                headerClass: aggridHeaderClass,
            },
            {
                headerName: '수량',
                colId: 'salesQty',
                field: 'salesQty',
                width: 100,
                cellStyle: (params) => getCellStyle(params, 'salesQty'),
                comparator: (valueA, valueB) => (valueA || 0) - (valueB || 0),
                valueFormatter: (params) =>
                    params.value !== null && params.value !== undefined
                        ? formatNumber(params.value)
                        : '',  //null/undefined면 빈 문자열
            },
            {
                headerName: '단가',
                colId: 'salesPrice',
                field: 'salesPrice',
                width: 120,
                cellStyle: (params) => getCellStyle(params, 'salesPrice'),
                comparator: (valueA, valueB) => (valueA || 0) - (valueB || 0),
                valueFormatter: (params) =>
                    params.value !== null && params.value !== undefined
                        ? formatNumber(params.value)
                        : '',  // null/undefined면 빈 문자열
            },
            // {
            //     headerName: '공급가',
            //     colId: 'supplyAmt',
            //     field: 'supplyAmt',
            //     width: 120,
            //     cellStyle: (params) => getCellStyle(params, 'supplyAmt'),
            //     comparator: (valueA, valueB) => (valueA || 0) - (valueB || 0),
            //     valueFormatter: (params) =>
            //         params.value ? formatNumber(params.value) : '0',
            // },
            // {
            //     headerName: '부가세',
            //     colId: 'vatAmt',
            //     field: 'vatAmt',
            //     width: 120,
            //     cellStyle: (params) => getCellStyle(params, 'vatAmt'),
            //     comparator: (valueA, valueB) => (valueA || 0) - (valueB || 0),
            //     valueFormatter: (params) =>
            //         params.value ? formatNumber(params.value) : '0',
            // },
            {
                headerName: '금액',
                colId: 'itemAmt',
                field: 'itemAmt',
                width: 120,
                cellStyle: (params) => getCellStyle(params, 'itemAmt'),
                comparator: (valueA, valueB) => (valueA || 0) - (valueB || 0),
                valueFormatter: (params) =>
                    params.value ? formatNumber(params.value) : '0',
            },
            {
                headerName: '비고',
                colId: 'remark',
                field: 'remark',
                width: 200,
                cellStyle: (params) => getCellStyle(params, 'remark'),
                headerClass: aggridHeaderClass,
            },
        ],
        [hoveredRowIndex, detailList]
    );

    // ========================================
    // Custom Column 정의 (columnState 반영)
    // ========================================
    const columnDefs: ColDef<SAL2060_R01DetailDto>[] = useMemo(() => {
        if (detailColumnState === null) {
            return baseColumnDefs;
        }

        return GridCustomColumnDefs<SAL2060_R01DetailDto>({
            baseColumnDefs,
            columnState: detailColumnState,
            headerNames,
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [detailColumnState, baseColumnDefs]);

    // ========================================
    // Pinned Bottom Row (합계)
    // ========================================
    const pinnedBottomRow = useMemo(
        () => [
            {
                rowIndex: '',
                itemCd: `${detailList.length.toLocaleString()}건`,
                salesQty: null,
                salesPrice: null,
                itemAmt: detailList
                    .reduce((sum, item) => sum + (item.itemAmt || 0), 0),
            } as any,
        ],
        [detailList]
    );

    // ========================================
    // UI 렌더링
    // ========================================
    return (
        <GridContextWrapper<SAL2060_R01DetailDto>
        	enableCellCalculation
            gridRef={gridRef}
            paddedGridData={paddedGridData as SAL2060_R01DetailDto[]}
            initSelected={() => {}}
            defaultRowData={defaultSAL2060_R01DetailDto}
            baseColumnDefs={columnDefs}
            setColumnState={setDetailColumnState}
            localStorageKey={COLUMN_STATE_KEY}
            menuItems={undefined}
        >
            <Grid_Touch<SAL2060_R01DetailDto>
                ref={gridRef}
                rowData={paddedGridData as SAL2060_R01DetailDto[]}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowId={getRowId}
                rowClassRules={createRowClassRules<SAL2060_R01DetailDto>({
                    idKey: 'salBillNo',
                    selectedList: [],
                    deletedList: [],
                })}
                onRowClicked={undefined}
                onCellKeyDown={undefined}
                pinnedBottomRowData={pinnedBottomRow}
                setHoveredRowIndex={setHoveredRowIndex}
                columnStateKey={COLUMN_STATE_KEY}
                setColumnState={setDetailColumnState}
            />
        </GridContextWrapper>
    );
};

export { DetailTable_SAL2060_R01 };
export default DetailTable_SAL2060_R01;
